import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { T, Modal } from './theme';
import { listBooks } from '../services/bookService';
import { useAuth } from './AuthContext';

const CATEGORIES = ['All', 'Agama', 'Vaikhanasa', 'Sanskrit', 'Philosophy', 'Ritual', 'History', 'Other'];
const PER_PAGE = 12;
const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

const BookDetailModal = ({ book, onClose }) => (
  <Modal onClose={onClose} title={book.title} subtitle={book.category} wide>
    <div className="flex flex-col sm:flex-row gap-6">
      <div className="flex-shrink-0 mx-auto sm:mx-0">
        {book.coverImageUrl ? (
          <img src={book.coverImageUrl} alt={book.title}
            className="w-40 h-56 object-cover rounded-2xl"
            style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.15)', border: '2px solid rgba(212,175,55,0.2)' }} />
        ) : (
          <div className="w-40 h-56 rounded-2xl flex items-center justify-center text-5xl"
            style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyDark})` }}>📖</div>
        )}
      </div>
      <div className="flex-1 space-y-3">
        {book.subtitle && <p className="text-sm italic" style={{ color: T.muted }}>{book.subtitle}</p>}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {[
            ['Author', book.author],
            ['Publisher', book.publisher],
            ['Language', book.language],
            ['Edition', book.edition],
            ['Pages', book.pages],
            ['ISBN', book.isbn],
            ['Publication Date', book.publicationDate],
            ['Category', book.category],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label}>
              <span className="vrv-label" style={{ fontSize: '0.6rem' }}>{label}</span>
              <p className="font-medium" style={{ color: T.ink }}>{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
    {book.description && (
      <div className="mt-5">
        <p className="vrv-label mb-2">Description</p>
        <p className="text-sm leading-relaxed" style={{ color: T.muted, whiteSpace: 'pre-wrap' }}>{book.description}</p>
      </div>
    )}
    <div className="mt-6 flex justify-end">
      <button onClick={onClose} className="btn-navy">Close</button>
    </div>
  </Modal>
);

const BookCard = ({ book, onClick }) => (
  <motion.div variants={fadeUp} className="vrv-card overflow-hidden flex flex-col cursor-pointer" onClick={onClick}>
    <div className="relative overflow-hidden" style={{ height: '220px' }}>
      {book.coverImageUrl ? (
        <img src={book.coverImageUrl} alt={book.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-6xl"
          style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyDark})` }}>📖</div>
      )}
      {book.category && (
        <span className="absolute top-3 left-3 badge badge-gold" style={{ fontSize: '9px' }}>{book.category}</span>
      )}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-bold text-base leading-snug mb-1" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{book.title}</h3>
      {book.author && <p className="text-xs mb-1" style={{ color: T.muted }}>by {book.author}</p>}
      {book.publisher && <p className="text-xs mb-3" style={{ color: T.muted }}>{book.publisher}</p>}
      {book.description && (
        <p className="text-xs leading-relaxed flex-1 line-clamp-3 mb-4" style={{ color: T.muted }}>{book.description}</p>
      )}
      <button
        className="w-full text-xs font-bold uppercase tracking-wider py-2.5 rounded-xl transition-all duration-200"
        style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyDark})`, color: 'white', fontFamily: 'Poppins, sans-serif' }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
        View Details
      </button>
    </div>
  </motion.div>
);

const BookCatalogPage = () => {
  const { role } = useAuth();
  const isAdmin = (role || '').split(',').map(s => s.trim().toUpperCase()).includes('ADMIN');
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [selectedBook, setSelectedBook] = useState(null);
  const [page, setPage] = useState(1);

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listBooks({ activeOnly: 'true' });
      setBooks(data.books || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBooks(); }, [fetchBooks]);
  useEffect(() => { setPage(1); }, [search, category]);

  const filtered = books.filter(b => {
    const matchCat = category === 'All' || b.category === category;
    const q = search.toLowerCase();
    const matchSearch = !q || [b.title, b.author, b.publisher, b.description, b.category]
      .some(v => String(v || '').toLowerCase().includes(q));
    return matchCat && matchSearch;
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div style={{ background: T.bg, minHeight: '100vh', color: T.ink }}>
      {/* Hero */}
      <section className="vrv-hero vrv-hero-no-watermark">
        <div className="vrv-container">
          <div className="relative z-10 max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Book Catalog
            </h1>
            <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Explore our collection of Vaikhanasa scriptures, agamas, and sacred literature.
            </p>
            {isAdmin && (
              <button onClick={() => navigate('/content-management', { state: { section: 'books', subTab: 'manage' } })} className="btn-navy text-xs mb-4"
                style={{ background: T.gold, color: T.navy, fontWeight: 700 }}>
                ✏️ Manage Catalog
              </button>
            )}
            <div className="grid grid-cols-2 gap-3 max-w-xs">
              <div className="vrv-kpi-card">
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{books.length}</p>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>Total Books</p>
              </div>
              <div className="vrv-kpi-card">
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {new Set(books.map(b => b.category).filter(Boolean)).size}
                </p>
                <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>Categories</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="vrv-container py-10">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: T.muted }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Search by title, author, publisher…" value={search}
              onChange={e => setSearch(e.target.value)}
              className="vrv-input pl-10" style={{ minHeight: '44px' }} />
          </div>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="vrv-select" style={{ minHeight: '44px', width: 'auto', minWidth: '160px' }}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
          </select>
        </div>

        <p className="text-sm mb-6" style={{ color: T.muted }}>
          {filtered.length} book{filtered.length !== 1 ? 's' : ''}{category !== 'All' ? ` in ${category}` : ''}
        </p>

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="vrv-card overflow-hidden">
                <div className="shimmer" style={{ height: '220px' }} />
                <div className="p-5 space-y-3">
                  <div className="shimmer h-4 w-3/4 rounded-lg" />
                  <div className="shimmer h-3 w-1/2 rounded-lg" />
                  <div className="shimmer h-8 rounded-xl mt-3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grid */}
        {!loading && paginated.length > 0 && (
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }}
            variants={{ show: { transition: { staggerChildren: 0.05 } } }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {paginated.map(book => (
              <BookCard key={book.bookId} book={book} onClick={() => setSelectedBook(book)} />
            ))}
          </motion.div>
        )}

        {/* Empty */}
        {!loading && filtered.length === 0 && (
          <div className="vrv-card text-center py-20">
            <div className="text-5xl mb-4">📚</div>
            <p className="font-bold text-lg mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>No Books Found</p>
            <p className="text-sm" style={{ color: T.muted }}>
              {search || category !== 'All' ? 'Try adjusting your search or filter.' : 'The catalog is being updated. Check back soon.'}
            </p>
            {(search || category !== 'All') && (
              <button onClick={() => { setSearch(''); setCategory('All'); }} className="btn-outline mt-4" style={{ fontSize: '0.75rem' }}>
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}
              className="px-4 py-2 text-xs font-bold rounded-xl transition-all disabled:opacity-40"
              style={{ border: '1.5px solid rgba(0,0,0,0.1)', color: T.muted }}>‹ Prev</button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const pg = page <= 4 ? i + 1 : page - 3 + i;
              if (pg < 1 || pg > totalPages) return null;
              return (
                <button key={pg} onClick={() => setPage(pg)}
                  className="w-9 h-9 text-xs font-bold rounded-xl transition-all"
                  style={page === pg
                    ? { background: T.navy, color: 'white', border: `1px solid ${T.navy}` }
                    : { border: '1.5px solid rgba(0,0,0,0.1)', color: T.muted }}>
                  {pg}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}
              className="px-4 py-2 text-xs font-bold rounded-xl transition-all disabled:opacity-40"
              style={{ border: '1.5px solid rgba(0,0,0,0.1)', color: T.muted }}>Next ›</button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedBook && <BookDetailModal book={selectedBook} onClose={() => setSelectedBook(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default BookCatalogPage;
